// src/pages/Pagamentos/SolicitarModulo.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  CardActions, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert,
  Divider, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  HourglassEmpty as TrialIcon,
  Lock as InactiveIcon,
  Send as SendIcon,
  Extension as ModuleIcon
} from '@mui/icons-material';
import { listarCatalogoModulos, listarMeusModulos, solicitarModulo } from '../../services/billingService';

const METODOS = [
  { value: 'MPESA',               label: 'M-Pesa' },
  { value: 'EMOLA',               label: 'E-mola' },
  { value: 'TRANSFERENCIA_BANCARIA', label: 'Transferência Bancária' },
  { value: 'BIM',                 label: 'BIM' },
  { value: 'BCI',                 label: 'BCI' },
  { value: 'MOZABANK',            label: 'MozaBanco' },
  { value: 'STANDARDBANK',        label: 'Standard Bank' },
];

export default function SolicitarModulo() {
  const [catalogo, setCatalogo]   = useState([]);
  const [ativos, setAtivos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [erro, setErro]           = useState('');
  const [sucesso, setSucesso]     = useState('');

  // Dialog
  const [dialogAberto, setDialogAberto] = useState(false);
  const [moduloSelecionado, setModuloSelecionado] = useState(null);
  const [form, setForm] = useState({
    tipo_cobranca: 'MENSAL',
    metodo_pagamento: '',
    referencia_pagamento: '',
    observacoes: ''
  });
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    Promise.all([listarCatalogoModulos(), listarMeusModulos()])
      .then(([cat, meus]) => {
        setCatalogo(cat.modulos ?? []);
        setAtivos(meus.modulos ?? []);
      })
      .catch(e => setErro(e.response?.data?.erro || 'Erro ao carregar módulos'))
      .finally(() => setLoading(false));
  }, []);

  const getEstadoModulo = (codigo) => {
    const m = ativos.find(a => a.codigo === codigo);
    if (!m || !m.ativo) return 'inativo';
    if (m.em_trial) {
      const expirou = m.trial_fim && new Date(m.trial_fim) < new Date();
      return expirou ? 'trial_expirado' : 'trial';
    }
    return 'ativo';
  };

  const getTrialFim = (codigo) => {
    const m = ativos.find(a => a.codigo === codigo);
    return m?.trial_fim ? new Date(m.trial_fim).toLocaleDateString('pt-PT') : null;
  };

  const abrirDialog = (modulo) => {
    setModuloSelecionado(modulo);
    setForm({ tipo_cobranca: 'MENSAL', metodo_pagamento: '', referencia_pagamento: '', observacoes: '' });
    setDialogAberto(true);
  };

  const enviarSolicitacao = async () => {
    if (!form.metodo_pagamento) return;
    setEnviando(true);
    try {
      await solicitarModulo({
        modulo_codigo: moduloSelecionado.codigo,
        ...form
      });
      setSucesso(`Pedido de activação do módulo "${moduloSelecionado.nome}" registado. Aguarde confirmação do Zenkroll.`);
      setDialogAberto(false);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao submeter pedido');
    } finally {
      setEnviando(false);
    }
  };

  const valorModulo = (modulo) => {
    const v = form.tipo_cobranca === 'ANUAL'
      ? (modulo.preco_anual_mzn ?? modulo.preco_mensal_mzn * 10)
      : modulo.preco_mensal_mzn;
    return v ? (v / 100).toLocaleString('pt-MZ') + ' MZN' : '—';
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h5" fontWeight={700}>Módulos Disponíveis</Typography>
        <Typography variant="body2" color="text.secondary">
          Subscreva módulos adicionais para a sua instituição. Após o pagamento, envie o comprovativo e o módulo será activado em menos de 24h.
        </Typography>
      </Box>

      {erro    && <Alert severity="error"   onClose={() => setErro('')}    sx={{ mb: 2 }}>{erro}</Alert>}
      {sucesso && <Alert severity="success" onClose={() => setSucesso('')} sx={{ mb: 2 }}>{sucesso}</Alert>}

      <Grid container spacing={3}>
        {catalogo.map(modulo => {
          const estado = getEstadoModulo(modulo.codigo);
          const trialFim = getTrialFim(modulo.codigo);
          const isAddon = modulo.eh_addon;

          return (
            <Grid item xs={12} sm={6} md={4} key={modulo.codigo}>
              <Card
                elevation={estado === 'ativo' ? 3 : 1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: estado === 'ativo' ? '2px solid' : '1px solid',
                  borderColor: estado === 'ativo' ? 'success.main' : estado === 'trial' ? 'warning.main' : 'divider',
                  position: 'relative'
                }}
              >
                {isAddon && (
                  <Chip label="Add-on" size="small" color="info" sx={{ position: 'absolute', top: 12, right: 12 }} />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ModuleIcon color={estado === 'ativo' ? 'success' : 'action'} />
                    <Typography variant="h6" fontWeight={600}>{modulo.nome}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {modulo.descricao}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Mensal</Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {modulo.preco_mensal_mzn ? (modulo.preco_mensal_mzn / 100).toLocaleString('pt-MZ') : '—'} MZN
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary">Anual</Typography>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {modulo.preco_anual_mzn ? (modulo.preco_anual_mzn / 100).toLocaleString('pt-MZ') : '—'} MZN
                      </Typography>
                    </Box>
                  </Box>

                  {/* Estado actual */}
                  <Box mt={2}>
                    {estado === 'ativo' && (
                      <Chip icon={<ActiveIcon />} label="Activo" color="success" size="small" />
                    )}
                    {estado === 'trial' && (
                      <Chip icon={<TrialIcon />} label={`Trial até ${trialFim}`} color="warning" size="small" />
                    )}
                    {estado === 'trial_expirado' && (
                      <Chip icon={<InactiveIcon />} label="Trial expirado" color="error" size="small" />
                    )}
                    {estado === 'inativo' && (
                      <Chip icon={<InactiveIcon />} label="Não subscrito" color="default" size="small" />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  {estado === 'ativo' ? (
                    <Button fullWidth variant="outlined" color="success" disabled>
                      Já activo
                    </Button>
                  ) : (
                    <Button
                      fullWidth variant="contained"
                      color={estado === 'trial' ? 'warning' : 'primary'}
                      startIcon={<SendIcon />}
                      onClick={() => abrirDialog(modulo)}
                    >
                      {estado === 'trial' ? 'Subscrever antes do fim do trial' : 'Subscrever'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dialog de solicitação */}
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Solicitar activação — {moduloSelecionado?.nome}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Efectue o pagamento e preencha os dados abaixo. O módulo será activado após confirmação da equipa Zenkroll (normalmente em menos de 24h úteis).
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Tipo de cobrança"
                value={form.tipo_cobranca}
                onChange={e => setForm(f => ({ ...f, tipo_cobranca: e.target.value }))}
              >
                <MenuItem value="MENSAL">Mensal</MenuItem>
                <MenuItem value="ANUAL">Anual (10% desconto)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Valor a pagar" disabled
                value={moduloSelecionado ? valorModulo(moduloSelecionado) : ''}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select required fullWidth label="Método de pagamento"
                value={form.metodo_pagamento}
                onChange={e => setForm(f => ({ ...f, metodo_pagamento: e.target.value }))}
              >
                {METODOS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Referência / número de transacção"
                value={form.referencia_pagamento}
                onChange={e => setForm(f => ({ ...f, referencia_pagamento: e.target.value }))}
                placeholder="Ex: 84 123 4567 ou número de transacção"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Observações (opcional)"
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
          <Button
            variant="contained" color="primary"
            onClick={enviarSolicitacao}
            disabled={enviando || !form.metodo_pagamento}
            startIcon={enviando ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {enviando ? 'A enviar...' : 'Enviar pedido'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
